import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Search, ExternalLink, CheckCircle2, X, Loader2, Building, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import SavedSearchesBar from "./SavedSearchesBar";

const money = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

/**
 * Pull from Crexi — async run + poll.
 *
 * Flow (post-iter59):
 *   1. POST /api/crexi/search → backend kicks off Apify run, returns runId
 *      immediately (≤ 5s — fits the 60s ingress cap).
 *   2. Frontend polls GET /api/crexi/runs/{runId} every 5s.
 *   3. When status flips to SUCCEEDED, backend fetches the dataset, normalises
 *      and stages into `comps_staged`. Frontend reloads staged list.
 *
 * This bypasses the previous timeout-and-die behaviour.
 */
export default function CrexiPullDialog({ open, onOpenChange, onImported }) {
  const [meta, setMeta] = useState({ configured: false, states: [], property_types: [] });
  const [states, setStates] = useState(["NC"]);
  const [propertyTypes, setPropertyTypes] = useState(["Office"]);
  const [maxProperties, setMaxProperties] = useState(10);
  const [searching, setSearching] = useState(false);
  const [runState, setRunState] = useState(null); // { run_id, status, items_staged }
  const [staged, setStaged] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const stateRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!open) {
      if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
      return;
    }
    api.get("/crexi/status").then((r) => setMeta(r.data || {})).catch(() => setMeta({ configured: false }));
    refreshStaged();
    setTimeout(() => stateRef.current?.focus(), 150);
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [open]);

  const refreshStaged = async () => {
    try {
      const r = await api.get("/loopnet/staged");
      const crexiRows = (r.data.items || []).filter((i) => i.source === "crexi");
      setStaged(crexiRows);
      setSelected(new Set(crexiRows.map((i) => i.id)));
    } catch {
      setStaged([]);
    }
  };

  const toggleArrayItem = (arr, setter, value) => {
    setter(arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]);
  };

  const search = async (e) => {
    e?.preventDefault();
    if (!states.length) {
      toast.error("Pick at least one state");
      return;
    }
    setSearching(true);
    setRunState(null);
    try {
      const r = await api.post("/crexi/search", {
        states,
        property_types: propertyTypes.length ? propertyTypes : null,
        max_properties: Math.min(Math.max(Number(maxProperties) || 10, 1), 100),
      });
      const { run_id, status } = r.data || {};
      if (!run_id) throw new Error("No run id returned");
      toast.info("Crexi run started — polling for results");
      setRunState({ run_id, status: status || "RUNNING", items_staged: 0 });
      pollRun(run_id);
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiError(err.response?.data?.detail);
      if (status === 503) toast.error("Crexi not configured (APIFY_API_TOKEN missing)");
      else if (status === 502) toast.error(detail || "Apify upstream error");
      else toast.error(detail || "Search failed");
      setSearching(false);
    }
  };

  const pollRun = async (runId) => {
    try {
      const r = await api.get(`/crexi/runs/${runId}`);
      const { status, items_staged, finished_at } = r.data || {};
      setRunState((prev) => ({ ...(prev || {}), status, items_staged, finished_at }));
      if (status === "SUCCEEDED" || status === "FAILED" || status === "ABORTED" || status === "TIMED-OUT") {
        setSearching(false);
        if (status === "SUCCEEDED") {
          toast.success(`Run complete — ${items_staged || 0} new listing${items_staged === 1 ? "" : "s"} staged`);
          await refreshStaged();
        } else {
          toast.error(`Run ${status}`);
        }
        return;
      }
      // Still running — poll again in 5s
      pollRef.current = setTimeout(() => pollRun(runId), 5000);
    } catch (err) {
      setSearching(false);
      toast.error(formatApiError(err.response?.data?.detail) || "Polling failed");
    }
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === staged.length) setSelected(new Set());
    else setSelected(new Set(staged.map((i) => i.id)));
  };

  const importSelected = async () => {
    if (!selected.size) {
      toast.error("Pick at least one row");
      return;
    }
    setImporting(true);
    try {
      const r = await api.post("/loopnet/staged/import", { ids: Array.from(selected) });
      toast.success(`Imported ${r.data.imported} comp${r.data.imported === 1 ? "" : "s"}`);
      await refreshStaged();
      onImported?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const reject = async (id) => {
    try {
      await api.delete(`/loopnet/staged/${id}`);
      setStaged((prev) => prev.filter((i) => i.id !== id));
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not reject");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="crexi-pull-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <Building className="w-5 h-5 text-violet-600" /> Pull from Crexi
            <span className="ml-2 px-2 py-0.5 rounded-sm bg-violet-50 border border-violet-200 text-violet-900 text-[10px] uppercase tracking-widest font-mono">unofficial · via Apify · async</span>
          </DialogTitle>
        </DialogHeader>

        {!meta.configured && (
          <div className="text-xs bg-rose-50 border border-rose-200 text-rose-900 rounded-sm px-3 py-2">
            <b>Not configured.</b> Set <code>APIFY_API_TOKEN</code> in <code>backend/.env</code> and restart the backend.
          </div>
        )}

        <SavedSearchesBar
          source="crexi"
          currentParams={{
            states,
            property_types: propertyTypes.length ? propertyTypes : null,
            max_properties: Math.min(Math.max(Number(maxProperties) || 10, 1), 100),
          }}
          canSave={states.length > 0}
          onApply={(p) => {
            setStates(Array.isArray(p.states) ? p.states : []);
            setPropertyTypes(Array.isArray(p.property_types) ? p.property_types : []);
            setMaxProperties(p.max_properties || 10);
          }}
          onAfterRun={async (data) => {
            const runId = data?.run_id;
            if (!runId) return;
            toast.info("Crexi run started — polling for results");
            setSearching(true);
            setRunState({ run_id: runId, status: data?.status || "RUNNING", items_staged: 0 });
            pollRun(runId);
          }}
        />

        <form onSubmit={search} className="space-y-4" data-testid="crexi-search-form">
          <div>
            <div className="overline text-slate-600 mb-1.5">States</div>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto" ref={stateRef}>
              {(meta.states || []).filter((s) => s.length === 2).map((s) => {
                const active = states.includes(s);
                return (
                  <button
                    type="button"
                    key={s}
                    onClick={() => toggleArrayItem(states, setStates, s)}
                    className={`px-2.5 py-1.5 rounded-sm border text-xs font-mono ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                    data-testid={`crexi-state-${s}`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="overline text-slate-600 mb-1.5">Property types <span className="text-slate-400 normal-case font-sans tracking-normal">(optional — empty = all)</span></div>
            <div className="flex flex-wrap gap-1.5">
              {(meta.property_types || []).map((p) => {
                const active = propertyTypes.includes(p);
                return (
                  <button
                    type="button"
                    key={p}
                    onClick={() => toggleArrayItem(propertyTypes, setPropertyTypes, p)}
                    className={`px-3 py-1.5 rounded-full border text-xs ${active ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-700 border-slate-200 hover:border-violet-300"}`}
                    data-testid={`crexi-pt-${p.replace(/\s+/g, "-")}`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-end gap-3">
            <label className="flex-1 max-w-xs">
              <div className="overline text-slate-600 mb-1">Max listings</div>
              <input
                type="number"
                min="1"
                max="100"
                className="input-clean w-full"
                value={maxProperties}
                onChange={(e) => setMaxProperties(e.target.value)}
                data-testid="crexi-max"
              />
              <div className="text-[10px] text-slate-400 font-mono mt-1">~ ${(Number(maxProperties) * 0.01).toFixed(2)} Apify cost · 30-180s wait</div>
            </label>
            <button
              type="submit"
              disabled={searching || !meta.configured}
              className="btn-primary-dark inline-flex items-center justify-center gap-2 disabled:opacity-60 px-6 h-10"
              data-testid="crexi-search-btn"
            >
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {searching ? "Crexi run…" : "Run search"}
            </button>
          </div>
        </form>

        {/* Live run progress */}
        {runState && (
          <div className="mt-4 px-4 py-3 rounded-sm border border-violet-200 bg-violet-50/50 flex items-center gap-3" data-testid="crexi-run-status">
            {runState.status === "SUCCEEDED" ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            ) : runState.status === "FAILED" || runState.status === "ABORTED" ? (
              <X className="w-5 h-5 text-red-600 shrink-0" />
            ) : (
              <Loader2 className="w-5 h-5 text-violet-600 animate-spin shrink-0" />
            )}
            <div className="text-sm">
              <div className="font-bold text-slate-900">Apify run · {runState.status || "RUNNING"}</div>
              <div className="text-xs text-slate-500 font-mono">
                Run id: {runState.run_id?.slice(0, 12)}…
                {runState.items_staged != null && ` · ${runState.items_staged} staged`}
              </div>
            </div>
          </div>
        )}

        {/* Staged results */}
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="overline text-slate-600">Staged for review {staged.length > 0 && <span className="text-slate-900">({staged.length})</span>}</div>
            {staged.length > 0 && (
              <button onClick={toggleAll} type="button" className="text-xs text-slate-700 hover:text-slate-900 font-mono uppercase tracking-widest">
                {selected.size === staged.length ? "Deselect all" : "Select all"}
              </button>
            )}
          </div>

          {staged.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-12 border border-dashed border-slate-200 rounded-sm">
              No staged Crexi listings yet. Run a search above to populate this panel.
            </div>
          ) : (
            <div className="border border-slate-200 rounded-sm overflow-hidden max-h-[44vh] overflow-y-auto">
              <table className="w-full text-sm" data-testid="crexi-staged-table">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                  <tr className="text-left">
                    <th className="px-3 py-2 w-10"></th>
                    <th className="px-3 py-2 w-20">Photo</th>
                    <th className="px-3 py-2 overline text-slate-600">Address</th>
                    <th className="px-3 py-2 overline text-slate-600">Type</th>
                    <th className="px-3 py-2 overline text-slate-600 text-right">Sqft</th>
                    <th className="px-3 py-2 overline text-slate-600 text-right">Price</th>
                    <th className="px-3 py-2 overline text-slate-600 text-right">Cap</th>
                    <th className="px-3 py-2 w-16 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {staged.map((c) => {
                    const checked = selected.has(c.id);
                    return (
                      <tr
                        key={c.id}
                        onClick={() => toggleRow(c.id)}
                        className={`border-b border-slate-100 last:border-0 cursor-pointer ${checked ? "bg-violet-50/60" : "hover:bg-slate-50"}`}
                        data-testid={`crexi-staged-row-${c.id}`}
                      >
                        <td className="px-3 py-2"><input type="checkbox" readOnly checked={checked} /></td>
                        <td className="px-3 py-2">
                          {c.thumbnail_url ? (
                            <img src={c.thumbnail_url} alt="" className="w-14 h-10 object-cover rounded-sm border border-slate-200" />
                          ) : (
                            <div className="w-14 h-10 bg-slate-100 border border-slate-200 rounded-sm flex items-center justify-center text-slate-300">
                              <ImageIcon className="w-4 h-4" />
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-semibold text-slate-900 truncate max-w-xs" title={c.formatted_address || c.address}>
                            {c.formatted_address || c.address}
                          </div>
                          {c.submarket && <div className="text-[10px] text-slate-400">{c.submarket}</div>}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600 capitalize">{(c.property_type || "other").replace(/_/g, " ")}</td>
                        <td className="px-3 py-2 text-right font-mono">{c.sqft ? c.sqft.toLocaleString() : "—"}</td>
                        <td className="px-3 py-2 text-right font-mono font-bold">{c.sale_price ? `$${money(c.sale_price)}` : "On Request"}</td>
                        <td className="px-3 py-2 text-right font-mono">{c.cap_rate != null ? `${c.cap_rate.toFixed(2)}%` : "—"}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          {c.external_url && (
                            <a href={c.external_url} target="_blank" rel="noreferrer" className="inline-block text-slate-400 hover:text-slate-700 p-1" title="Open in Crexi" data-testid={`crexi-open-${c.id}`}>
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                          <button onClick={() => reject(c.id)} className="inline-block text-slate-400 hover:text-red-600 p-1" title="Reject" data-testid={`crexi-reject-${c.id}`}>
                            <X className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <button type="button" onClick={() => onOpenChange(false)} className="btn-outline-dark">Close</button>
          <button
            type="button"
            onClick={importSelected}
            disabled={importing || !selected.size}
            className="btn-primary-dark inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="crexi-import-btn"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Import {selected.size || ""} to library
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
